import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from 'react';
import moment, { Moment } from 'moment';
import { keyBy } from 'lodash';
import {
  FormItem,
  H1,
  DatePicker,
  FormContext,
  FormContextType,
  Option,
  Select,
  TimePicker,
  InputNumber,
  Flex,
  FlexItem,
  NoLabel,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import {
  PositionTemplateFieldType,
  Resources,
  InexactDurationUnit,
  Mutations,
} from '@erecruit/types';
import { TIME_PARAM_FORMAT, DEFAULT_SHIFT_FORMAT } from '../../../../constants';

import { PositionField } from '../../types';
import { calculateEndDate, findShiftOption, getDuration } from './helpers';

import { Disclaimer } from '../Disclaimer';
import { generateFieldOptions } from '../../helpers';
import { fetchData } from '../DynamicFormItems/helpers';

const { FormattedMessage, injectIntl } = Intl;

const messages = messageIds.vms.newPositionWizard.schedule;

const calculateShiftTime = (shiftType: Resources.ShiftType) => ({
  startTime: moment(shiftType.startTime || '8:00:00', DEFAULT_SHIFT_FORMAT),
  endTime: moment(shiftType.endTime || '16:00:00', DEFAULT_SHIFT_FORMAT),
});

export interface ScheduleStepForm {
  durationAmount?: number;
  durationUnit?: InexactDurationUnit;
  estimatedStartDate: Moment;
  estimatedEndDate: Moment;
  shiftTypeId?: number;
  startTime: Moment;
  endTime: Moment;
}

interface ScheduleStepProps {
  fields: PositionField[];
  isEdit?: boolean;
}

export const ScheduleStep: FunctionComponent<ScheduleStepProps &
  Intl.InjectedIntlProps> = ({ intl, fields, isEdit }) => {
  const keyFields = keyBy(fields, 'fieldType');
  const { setFieldsValue, getFieldsValue } = useContext<
    FormContextType<ScheduleStepForm>
  >(FormContext);

  const shiftTypeField = keyFields[PositionTemplateFieldType.ShiftTypes];
  const estimatedStartDateField =
    keyFields[PositionTemplateFieldType.EstimatedStartDate];
  const estimatedEndDateField =
    keyFields[PositionTemplateFieldType.EstimatedEndDate];
  const estimatedStartTimeField =
    keyFields[PositionTemplateFieldType.StartTime];
  const estimatedEndTimeField = keyFields[PositionTemplateFieldType.EndTime];
  const workingDurationField = keyFields[PositionTemplateFieldType.Duration];

  const [shiftTypes, setShiftTypes] = useState<Resources.ShiftType[]>(
    shiftTypeField?.data as Resources.ShiftType[],
  );

  const shiftTypeValue: unknown =
    shiftTypeField?.value || shiftTypes?.[1]?.id || '';

  const defaultStartDate = estimatedStartDateField.value
    ? moment(estimatedStartDateField.value, 'YYYY-MM-DD').set({
        hour: 12,
        minute: 0,
        second: 0,
      })
    : moment();

  const defaultEndDate = estimatedEndDateField.value
    ? moment(estimatedEndDateField.value, 'YYYY-MM-DD').set({
        hour: 12,
        minute: 0,
        second: 0,
      })
    : moment().add(1, 'day');

  const [defaultDurationAmount, defaultDurationUnit] = getDuration(
    defaultStartDate,
    defaultEndDate,
  );

  const {
    durationAmount = defaultDurationAmount || 1,
    durationUnit = defaultDurationUnit || InexactDurationUnit.Day,
    estimatedStartDate = defaultStartDate,
    estimatedEndDate = defaultEndDate,
    shiftTypeId = shiftTypeValue,
    startTime,
    endTime,
  } = getFieldsValue();

  const getShift = (shiftId: number) =>
    shiftTypes && shiftTypes.find(shift => shift.id === shiftId);

  const handleDurationUnitChange = (unit: InexactDurationUnit) => {
    setFieldsValue({
      durationUnit: unit,
      estimatedEndDate:
        durationAmount && unit
          ? calculateEndDate(estimatedStartDate, durationAmount, unit)
          : undefined,
    });
  };
  const handleDurationAmountChange = (amount?: number) => {
    setFieldsValue({
      durationAmount: amount,
      estimatedEndDate:
        amount && durationUnit
          ? calculateEndDate(estimatedStartDate, amount, durationUnit)
          : undefined,
    });
  };

  const handleStartDateChange = (startDate: Moment) => {
    const [newDurationAmount, newDurationUnit] = getDuration(
      startDate,
      estimatedEndDate,
    );

    setFieldsValue({
      estimatedStartDate: startDate,
      durationAmount: newDurationAmount,
      durationUnit: newDurationUnit,
      estimatedEndDate: calculateEndDate(
        startDate,
        newDurationAmount,
        newDurationUnit,
      ),
    });
  };

  const handleEndDateChange = (endDate: Moment) => {
    const [newDurationAmount, newDurationUnit] = getDuration(
      estimatedStartDate,
      endDate,
    );

    setFieldsValue({
      durationAmount: newDurationAmount,
      durationUnit: newDurationUnit,
      estimatedEndDate:
        newDurationAmount && newDurationUnit
          ? calculateEndDate(
              estimatedStartDate,
              newDurationAmount,
              newDurationUnit,
            )
          : undefined,
    });
  };

  const handleShiftTypeChange = (shiftTypeId: number) => {
    const shift = getShift(shiftTypeId);
    const { startTime, endTime } = shift
      ? calculateShiftTime(shift)
      : {
          startTime: undefined,
          endTime: undefined,
        };
    setFieldsValue({
      startTime,
      endTime,
    });
  };

  const shiftType = shiftTypeId ? getShift(shiftTypeId as number) : undefined;

  const shiftTime = shiftType
    ? calculateShiftTime(shiftType)
    : {
        startTime: undefined,
        endTime: undefined,
      };

  const handleTimeChange = (
    time: Moment,
    fieldName: 'startTime' | 'endTime',
  ) => {
    if (!shiftTypes || !shiftTypes.length) {
      return;
    }
    const shift =
      //format times as it is in shiftType to be comparable
      fieldName === nameof<ScheduleStepForm>(o => o.startTime)
        ? findShiftOption(
            shiftTypes,
            time.format(DEFAULT_SHIFT_FORMAT),
            endTime.format(DEFAULT_SHIFT_FORMAT),
          )
        : findShiftOption(
            shiftTypes,
            startTime.format(DEFAULT_SHIFT_FORMAT),
            time.format(DEFAULT_SHIFT_FORMAT),
          );
    setFieldsValue({
      shiftTypeId: (shift && shift.id) || shiftTypes[0].id,
      [fieldName]: time,
    });
  };

  useEffect(() => {
    if (shiftTypeField && !shiftTypes) {
      fetchData<Resources.ShiftType>(shiftTypeField, undefined).then(data => {
        setShiftTypes(data);
      });
    }
  }, [shiftTypeField, shiftTypes]);

  return (
    <div>
      {!isEdit && (
        <>
          <H1>
            <FormattedMessage id={messages.title} />
          </H1>
          <Disclaimer>
            <FormattedMessage id={messages.disclaimer} />
          </Disclaimer>
        </>
      )}
      <Flex flex={1} gutter={3}>
        {estimatedStartDateField && (
          <FormItem
            id="estimatedStartDate"
            label={intl.formatMessage({
              id: messages.estimatedStartDate,
            })}
            options={{
              rules: [
                {
                  type: 'object',
                  required:
                    estimatedStartDateField.settings &&
                    estimatedStartDateField.settings.required,
                  message: intl.formatMessage({
                    id: messageIds.vms.errors.form.requiredField,
                  }),
                },
              ],
              initialValue: estimatedStartDate,
            }}
          >
            <DatePicker
              onChange={value => value && handleStartDateChange(value)}
              disabled={
                estimatedStartDateField.settings &&
                estimatedStartDateField.settings.locked
              }
            />
          </FormItem>
        )}
        <Flex gutter={2} justify="space-between" flex={1}>
          <FlexItem>
            <FormItem
              id={nameof<Mutations.NewPositionParams>(o => o.durationAmount)}
              label={intl.formatMessage({
                id: messages.workingDuration,
              })}
              options={generateFieldOptions(
                workingDurationField,
                durationAmount,
              )}
            >
              <InputNumber
                style={{ minWidth: 0 }}
                min={0}
                max={999}
                onChange={(value?: number) =>
                  value && handleDurationAmountChange(value)
                }
                disabled={
                  workingDurationField.settings &&
                  workingDurationField.settings.locked
                }
              />
            </FormItem>
          </FlexItem>
          <FlexItem>
            {workingDurationField && (
              <FormItem
                id={nameof<Mutations.NewPositionParams>(o => o.durationUnit)}
                label={<NoLabel />}
                colon={false}
                options={{
                  initialValue: durationUnit,
                }}
              >
                <Select
                  dropdownMatchSelectWidth={false}
                  allowClear={false}
                  onSelect={(value: InexactDurationUnit) =>
                    handleDurationUnitChange(value)
                  }
                  disabled={
                    workingDurationField.settings &&
                    workingDurationField.settings.locked
                  }
                >
                  <Option key="days" value={InexactDurationUnit.Day}>
                    <FormattedMessage id={messageIds.vms.general.dayUnknown} />
                  </Option>
                  <Option key="weeks" value={InexactDurationUnit.Week}>
                    <FormattedMessage id={messageIds.vms.general.weekUnknown} />
                  </Option>
                  <Option key="months" value={InexactDurationUnit.Month}>
                    <FormattedMessage
                      id={messageIds.vms.general.monthUnknown}
                    />
                  </Option>
                  <Option key="years" value={InexactDurationUnit.Year}>
                    <FormattedMessage id={messageIds.vms.general.yearUnknown} />
                  </Option>
                </Select>
              </FormItem>
            )}
          </FlexItem>
        </Flex>
      </Flex>
      <Flex flex={1} gutter={3}>
        {estimatedEndDateField && (
          <FormItem
            id="estimatedEndDate"
            label={intl.formatMessage({
              id: messages.estimatedEndDate,
            })}
            options={{
              rules: [
                {
                  type: 'object',
                  required:
                    estimatedEndDateField.settings &&
                    estimatedEndDateField.settings.required,
                  message: intl.formatMessage({
                    id: messageIds.vms.errors.form.requiredField,
                  }),
                },
              ],
              initialValue:
                durationAmount && durationUnit
                  ? calculateEndDate(
                      estimatedStartDate,
                      durationAmount,
                      durationUnit,
                    )
                  : undefined,
            }}
          >
            <DatePicker
              onChange={value => value && handleEndDateChange(value)}
              disabledDate={(d: Moment | null) =>
                !!d && d.isBefore(estimatedStartDate)
              }
            />
          </FormItem>
        )}
        {shiftTypeField && (
          <FormItem
            id="shiftTypeId"
            label={intl.formatMessage({
              id: messages.shiftTypes,
            })}
            options={generateFieldOptions(shiftTypeField, shiftTypeId)}
          >
            <Select
              onSelect={(value: number) => handleShiftTypeChange(value)}
              disabled={
                shiftTypeField.settings && shiftTypeField.settings.locked
              }
            >
              {shiftTypes &&
                shiftTypes.map(element => (
                  <Option key={element.id} value={element.id}>
                    {element.name}
                  </Option>
                ))}
            </Select>
          </FormItem>
        )}
      </Flex>
      <Flex flex={1} gutter={3}>
        {estimatedStartTimeField && (
          <FormItem
            id="startTime"
            label={intl.formatMessage({
              id: messages.estimatedStartTime,
            })}
            options={generateFieldOptions(
              estimatedStartTimeField,
              shiftTime.startTime
                ? moment(shiftTime.startTime, TIME_PARAM_FORMAT)
                : undefined,
            )}
          >
            <TimePicker
              use12Hours={true}
              minuteStep={5}
              onChange={timeValue => handleTimeChange(timeValue, 'startTime')}
              disabled={
                estimatedStartTimeField.settings &&
                estimatedStartTimeField.settings.locked
              }
            />
          </FormItem>
        )}
        {estimatedEndTimeField && (
          <FormItem
            id="endTime"
            label={intl.formatMessage({
              id: messages.estimatedEndTime,
            })}
            options={generateFieldOptions(
              estimatedEndTimeField,
              shiftTime.endTime
                ? moment(shiftTime.endTime, TIME_PARAM_FORMAT)
                : undefined,
            )}
          >
            <TimePicker
              use12Hours={true}
              minuteStep={5}
              onChange={timeValue => handleTimeChange(timeValue, 'endTime')}
              disabled={
                estimatedEndTimeField.settings &&
                estimatedEndTimeField.settings.locked
              }
            />
          </FormItem>
        )}
      </Flex>
    </div>
  );
};

export default injectIntl(ScheduleStep);
